import React, { Component } from "react";
import AutoComplete from '../auto-complete';
import Input from '../input';
import Icon from '../icon';
import Alert from '../alert';
import Button from '../button';
import PagePoint from '../page-point/index';

const { Option } = AutoComplete;
const { Group } = Input;
const { pagePointConfig: { regKey } } = PagePoint;

declare const InputSizes: ["small", "default", "large"];

type SIProps = {
  onBlur: (() => void);
  onFocus: (() => void);
  prefix: React.ReactNode[]; 
  placeholder: string;
  optionRenderer: ((item: object) => string | object);
  tipText: string;
  enterButton: string;
  buttonClassName?: string;
  autocompleteClassName?: string;
  optionLabelProp: string;
  searchPromise: ((value: string) => Promise<any>);
  onSelect: ((value: string) => void);
  disabled: boolean;
  size: (typeof InputSizes)[number];
  onRemoveImage: (() => void);
  suffix: object[];
}
type SIState = {
  label: string;
  image: any;
  dataSource: any[];
  loading: boolean;
  showTips: boolean;
}

/**
 * 搜索框。根据用户在input中输入的内容，调用外部的接口获取AutoComplete数据。
 * 并且把用户最终的选择通过callback传递到外部组件中。
 */
class SearchInput extends Component<SIProps, SIState> {
  searchLabel: string;
  dataProvider: Map<any, any>;
  input: any;
  timer: any;

  static addSuffix(suffix, element) {
    if (!suffix.find(e => e.key === element.key)) {
      suffix.push(element);
    }
  }

  static getDerivedStateFromProps(props, state) {
    return {
      ...state,
      image: props.injectImage,
    };
  }

  constructor(props) {
    super(props);
    this.dataProvider = new Map();
    this.searchLabel = props.defaultValue;
    this.state = {
      image: null,
      dataSource: [],
      label: props.defaultValue || "",
      loading: false,
      showTips: false,
    };
  }

  getInputText = () => {
    const { label } = this.state;
    return label;
  };

  handleInputBlur = () => {
    const { onBlur } = this.props;
    if (typeof onBlur === "function") {
      onBlur();
    }
  };

  handleInputFocus = () => {
    const { label } = this.state;
    const { onFocus } = this.props;
    if (typeof onFocus === "function") {
      onFocus();
    }
    this.handleLabelChange(label);
  };

  handleClickClearIcon = () => {
    this.handleLabelChange("");
    this.doRemoveImage();
    this.input.focus();
  };

  toggleTipText = () => {
    const { tipText } = this.props;
    if (tipText && tipText.length > 0) {
      this.setState(state => ({
        showTips: !state.showTips,
      }));
    }
    // if (typeof e !== "undefined") {
    //   e.stopPropagation();
    // }
  };

  handleLabelChange = v => {
    let value = v;
    if (typeof value === "undefined") {
      value = this.searchLabel;
    }
    const { searchPromise } = this.props;
    const { showTips } = this.state;
    this.searchLabel = value;
    if (typeof searchPromise === "function") {
      const fetchQuery = () => {
        const { searchLabel } = this;
        if (value === searchLabel) {
          searchPromise(value).then(res => {
            const label = this.searchLabel;
            if (res.search === label) {
              const { dataSource } = res;
              this.setState({
                dataSource,
                loading: false,
              });
              if (showTips === true) {
                this.toggleTipText();
              }
            }
          });
        }
      };
      if (typeof this.timer !== "undefined") {
        try {
          clearTimeout(this.timer);
        } catch (e) {
          // Something wrong here.
        }
      }
      if (value && value.length > 0) {
        this.timer = setTimeout(fetchQuery, 500);
      } else {
        fetchQuery();
      }
      this.setState({
        label: value,
        loading: true,
      });
    }
  };

  doRemoveImage = () => {
    const { onRemoveImage } = this.props;
    if (typeof onRemoveImage === "function") {
      onRemoveImage();
    }
  };

  handleSelect = value => {
    const { onSelect } = this.props;
    if (typeof onSelect === "function") {
      const object = this.dataProvider.get(value);
      onSelect(object);
    }
    this.setState({
      label: value,
    });
  };

  handleConfirm = () => {
    const { onSelect } = this.props;
    if (typeof onSelect === "function") {
      const { label } = this.state;
      onSelect(label);
    }
  };

  defaultOptionRenderer = renderer => {
    return item => {
      let component;
      if (renderer) {
        component = renderer(item);
      } else {
        component = (
          <Option key={item.id} text={item.label}>
            {item.label}
          </Option>
        );
      }
      this.dataProvider.set(component.key, item);
      return component;
    };
  };

  renderTipContent = () => {
    const { showTips } = this.state;
    const { tipText } = this.props;
    if (showTips && tipText && tipText.length > 0) {
      return (
        <Alert
          data-spm="Clstips"
          style={{
            textAlign: "left",
            position: "absolute",
            zIndex: 2,
            backgroundColor: "#fff",
          }}
          className="extra-msg"
          afterClose={this.toggleTipText}
          message={tipText}
          closable
          type="info"
          showIcon
        />
      );
    }
  };

  render() {
    const {
      prefix = [], // input的前缀组件
      placeholder, // input中的提示文案。
      optionRenderer, // autoComplete的选项renderer
      tipText, // tips提示的文字，如果没有tips按钮就不会显示。
      enterButton, // 搜索按钮的文案
      // searchPromise, // 根据输入获取选项的promise
      // onSelect, // 使用用户最终选择的内容来做搜索
      disabled = false,
      size,
      optionLabelProp,
      suffix = [],
      buttonClassName,
      autocompleteClassName,
      [regKey]: dataRegId
    } = this.props as any;
    const { dataSource, label, loading } = this.state;
    const regKeyProp ={[regKey]: dataRegId};
    const { image } = this.state;
    if (image) {
      prefix.push(image);
    }

    const props = {
      value: label,
      onSelect: this.handleSelect,
      onSearch: this.handleLabelChange,
      onFocus: this.handleInputFocus,
      onBlur: this.handleInputBlur,
      optionLabelProp,
      className: autocompleteClassName,
      style: {
        flex: 1,
      },
      size,
      ref: input => {
        this.input = input;
      },
      dataSource: []
    };
    this.dataProvider.clear();
    if (typeof optionRenderer === "function") {
      props.dataSource = dataSource.map(
        this.defaultOptionRenderer(optionRenderer)
      );
    } else {
      props.dataSource = dataSource.map(this.defaultOptionRenderer(null));
    }
    const inputProps = {
      placeholder: image ? "" : placeholder,
      value: disabled ? "" : label,
      [regKey]: dataRegId,
      size,
      suffix,
      disabled,
      onKeyDown: e => {
        const st = this.state;
        const lb = st.label;
        const img = st.image;
        if (img && lb.length < 1 && e.key === "Backspace") {
          this.doRemoveImage();
        }
      },
    };

    if ((label && label.length > 0) || image) {
      SearchInput.addSuffix(
        inputProps.suffix,
        <Icon
          data-spm="Clear"
          key="extra-close"
          className="extra-icon show"
          onClick={this.handleClickClearIcon}
          type="close-circle"
          size="small"
        />
      );
    }

    if (loading || tipText) {
      SearchInput.addSuffix(
        inputProps.suffix,
        <Icon
          key="extra-info"
          className="extra-icon"
          onClick={this.toggleTipText}
          type={loading ? "loading" : "exclamation-circle-o"}
        />
      );
    }

    return (
      <div data-spm="Xiala">
        <Group compact size="large">
          {prefix}
          <AutoComplete {...props}>
            <Input {...inputProps} />
          </AutoComplete>
          <Button
            data-spm="Search"
            type="primary"
            size={size}
            className={buttonClassName}
            onClick={this.handleConfirm}
            {...regKeyProp}
          >
            {enterButton}
          </Button>
        </Group>
        {this.renderTipContent()}
      </div>
    );
  }
}

export default SearchInput;



// WEBPACK FOOTER //
// ./src/components/search-input/index.tsx